/* components/reports/AbcAnalysisReport.jsx — единый рендерер отчёта
   «ABC-анализ меню» v2 (Report.result: _schema:'f40', schema_version:2).
   Используется и детальной страницей (/reports/:id), и инлайн-канвасом чата
   (ABCCanvas) — зеркало InventoryAnalysisReport.

   Структура зеркалит промпт-методику:
     сводка (позиции/выручка/прибыль/средний FC) и данные файла;
     кликабельные теги приоритетов и статусов food cost — фильтруют таблицу;
     основная таблица: Приоритет | Блюдо | ABC | FC | Статус | Комментарий
     (+ продажи/выручка/прибыль в полном режиме);
     «Недостаточно данных», выводы, итоговый вывод.

   Все числа/категории посчитаны кодом на бэке (reports/abc_calc.py);
   compact-режим — для канваса в чате (меньше колонок, без панели файла). */

import React from 'react';
import { Icon } from '../ui/Icon.jsx';
import { AiSkippedNote } from './AiSkippedNote.jsx';
import { fmt } from '../../lib/format.js';
import {
  ABC_FC_ORDER, ABC_PRIORITY_ORDER, abcFc, abcPrio,
} from '../../lib/reports/abcMeta.js';

const { useMemo, useState } = React;

const money0 = (v) => (v == null ? '—' : fmt.money(v));
const pct1 = (v) => (v == null ? '—' : fmt.percent(v, { digits: 1 }));
const qty1 = (v) => (v == null ? '—'
  : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v));

function Sec({ title, hint, children }) {
  return (
    <section className="canvas-sec">
      {title && (
        <div className="canvas-sec__head">
          <h4 className="canvas-sec__title t-eyebrow">{title}</h4>
          {hint && <span className="canvas-sec__hint t-caption text-mute">{hint}</span>}
        </div>
      )}
      {children}
    </section>
  );
}

/* Кликабельный тег-фильтр (приоритет или статус FC) в стиле tagpill. */
function FilterTag({ meta, active, onToggle, count, compactLabel }) {
  return (
    <button
      type="button"
      className={`tagpill tagpill--${meta.tone} cv-tagbtn${active ? ' is-active' : ''}`}
      title={meta.title}
      aria-pressed={active}
      onClick={onToggle}
    >
      <Icon name={meta.icon} size={12} />
      {compactLabel ? meta.short : meta.label}
      {count != null && <span className="num cv-tagbtn__n">{count}</span>}
    </button>
  );
}

/* Тег в ячейке таблицы — тот же FilterTag, короткий лейбл. */
function CellTag({ meta, active, onToggle }) {
  return (
    <button
      type="button"
      className={`tagpill tagpill--${meta.tone} cv-tagbtn${active ? ' is-active' : ''}`}
      title={`${meta.title} · клик фильтрует таблицу`}
      aria-pressed={active}
      onClick={onToggle}
    >
      <Icon name={meta.icon} size={12} />
      {meta.short || meta.label}
    </button>
  );
}

/* Комбо-код ABC: три буквы с расшифровкой по осям. */
function ComboCode({ p }) {
  return (
    <span
      className="cv-abc num"
      title={`Продажи: ${p.abc_sales} · Выручка: ${p.abc_revenue} · Прибыль: ${p.abc_profit}`}
    >
      {p.combo}
    </span>
  );
}

/* ── панель данных файла ── */
function MetaPanel({ meta, compact }) {
  if (!meta) return null;
  const facts = [
    meta.period && { icon: 'calendar', text: meta.period },
    meta.store && { icon: 'building', text: meta.store },
    !compact && meta.thresholds && { icon: 'percent', text: meta.thresholds },
  ].filter(Boolean);
  const files = compact ? [] : (meta.source_files || []);
  const warnings = meta.warnings || [];
  if (!facts.length && !files.length && !warnings.length) return null;
  return (
    <Sec title="Данные файла">
      <div className="inv2-valid">
        {facts.length > 0 && (
          <div className="inv2-valid__row">
            {facts.map((f, i) => (
              <span className="inv2-valid__fact" key={i}><Icon name={f.icon} size={13} /> {f.text}</span>
            ))}
          </div>
        )}
        {files.map((f, i) => (
          <div className="inv2-file" key={i}>
            <Icon name="file" size={14} />
            <span className="cv-strong truncate">{f}</span>
            {meta.parse_method === 'llm' && <span className="text-mute t-caption">распознано ИИ</span>}
          </div>
        ))}
        {warnings.length > 0 && (
          <div className="callout callout--warn inv2-callout" role="note">
            <Icon name="alert-triangle" size={15} />
            <div>{warnings.map((s, i) => <div key={i}>{s}</div>)}</div>
          </div>
        )}
      </div>
    </Sec>
  );
}

/* ── основная таблица позиций ── */
const COMPACT_ROW_LIMIT = 12;

function PositionsTable({ rows = [], compact, filter, onToggle }) {
  const matched = filter
    ? rows.filter((p) => (filter.kind === 'priority' ? p.priority : p.fc_status) === filter.id)
    : rows;
  // compact (чат-канвас): топ по приоритету, полный список — в отчёте
  const shown = compact ? matched.slice(0, COMPACT_ROW_LIMIT) : matched;
  const filterMeta = filter
    ? (filter.kind === 'priority' ? abcPrio(filter.id) : abcFc(filter.id))
    : null;
  if (!rows.length) return <p className="t-caption text-mute">Нет позиций.</p>;
  return (
    <div className="cv-table-wrap abc2-wrap">
      {filter && (
        <p className="t-caption text-mute inv2-filternote">
          Фильтр: «{filterMeta.label}» — {matched.length} из {rows.length}.{' '}
          <button type="button" className="inv2-reset" onClick={() => onToggle(filter.kind, filter.id)}>Сбросить</button>
        </p>
      )}
      <table className="cv-table inv2-table abc2-table">
        <thead>
          <tr>
            <th className="num">#</th>
            <th>Приоритет</th>
            <th>Блюдо</th>
            <th>ABC</th>
            <th className="num">FC</th>
            {!compact && <th className="num">Продажи</th>}
            {!compact && <th className="num">Выручка</th>}
            {!compact && <th className="num">Прибыль</th>}
            <th>Статус</th>
            <th>Комментарий</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((p) => (
            <tr key={p.n}>
              <td className="num text-mute" data-label="#">{p.n}</td>
              <td data-label="Приоритет">
                <CellTag
                  meta={abcPrio(p.priority)}
                  active={!!filter && filter.kind === 'priority' && filter.id === p.priority}
                  onToggle={() => onToggle('priority', p.priority)}
                />
              </td>
              <td data-label="Блюдо"><span className="cv-strong">{p.name}</span></td>
              <td data-label="ABC"><ComboCode p={p} /></td>
              <td className="num" data-label="FC">{pct1(p.fc_pct)}</td>
              {!compact && <td className="num" data-label="Продажи">{qty1(p.qty)}</td>}
              {!compact && <td className="num" data-label="Выручка">{money0(p.revenue)}</td>}
              {!compact && <td className="num" data-label="Прибыль">{money0(p.profit)}</td>}
              <td data-label="Статус">
                <span className="abc2-status">
                  <CellTag
                    meta={abcFc(p.fc_status)}
                    active={!!filter && filter.kind === 'fc' && filter.id === p.fc_status}
                    onToggle={() => onToggle('fc', p.fc_status)}
                  />
                  {!compact && <span className="t-caption text-mute">{p.status_label}</span>}
                </span>
              </td>
              <td data-label="Комментарий" className="cv-comment"><span className="text-dim">{p.comment}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      {compact && matched.length > shown.length && (
        <p className="t-caption text-mute">
          Показаны первые {shown.length} из {matched.length} — полная таблица в отчёте.
        </p>
      )}
    </div>
  );
}

function AbcAnalysisReport({ r, compact = false }) {
  // единый фильтр таблицы: {kind:'priority'|'fc', id} | null; клик по тому же
  // тегу снимает фильтр (зеркало InventoryAnalysisReport.activeCrit)
  const [filter, setFilter] = useState(null);
  const toggle = (kind, id) => setFilter(
    (cur) => (cur && cur.kind === kind && cur.id === id ? null : { kind, id }),
  );

  const totals = r.totals || {};
  const positions = r.positions || [];
  const prioCounts = totals.priorities || {};
  const fcCounts = useMemo(() => {
    const out = {};
    positions.forEach((p) => { out[p.fc_status] = (out[p.fc_status] || 0) + 1; });
    return out;
  }, [positions]);

  const avgFc = totals.avg_fc_pct;
  const metricItems = [
    { label: 'Позиций в анализе', value: totals.analyzed != null ? totals.analyzed : '—' },
    { label: 'Выручка', value: money0(totals.revenue) },
    { label: 'Прибыль', value: money0(totals.profit), tone: 'info' },
    {
      label: 'Средний food cost',
      value: pct1(avgFc),
      tone: avgFc == null ? undefined : (avgFc < 25 ? 'ok' : avgFc < 30 ? 'warn' : 'bad'),
    },
  ];

  const groups = totals.groups || {};
  const groupsHint = ['A', 'B', 'C'].map((g) => `${g}: ${groups[g] != null ? groups[g] : '—'}`).join(' · ');

  return (
    <>
      <div className="canvas-metrics" style={{ '--cm-count': metricItems.length }}>
        {metricItems.map((m, i) => (
          <div className={'canvas-metric' + (m.tone ? ' canvas-metric--' + m.tone : '')} key={i}>
            <span className="canvas-metric__val num">{m.value}</span>
            <span className="canvas-metric__lbl t-caption">{m.label}</span>
          </div>
        ))}
      </div>

      <AiSkippedNote r={r} />
      <MetaPanel meta={r.meta} compact={compact} />

      <Sec title="Приоритеты" hint="клик по тегу фильтрует таблицу">
        <div className="cv-chips">
          {ABC_PRIORITY_ORDER.map((id) => (
            <FilterTag
              key={id} meta={abcPrio(id)}
              active={!!filter && filter.kind === 'priority' && filter.id === id}
              onToggle={() => toggle('priority', id)}
              count={prioCounts[id] || 0}
            />
          ))}
        </div>
      </Sec>

      <Sec title="Food cost" hint={compact ? 'клик по тегу фильтрует таблицу' : 'шкала: <20 норма · 20–25 выше цели · 25–30 критично · ≥30 срочно'}>
        <div className="cv-chips">
          {ABC_FC_ORDER.filter((id) => fcCounts[id]).map((id) => (
            <FilterTag
              key={id} meta={abcFc(id)}
              active={!!filter && filter.kind === 'fc' && filter.id === id}
              onToggle={() => toggle('fc', id)}
              count={fcCounts[id]}
            />
          ))}
        </div>
      </Sec>

      <Sec
        title={`Позиции (${positions.length})`}
        hint={`группы по продажам — ${groupsHint}`}
      >
        <PositionsTable rows={positions} compact={compact} filter={filter} onToggle={toggle} />
      </Sec>

      {(r.insufficient || []).length > 0 && (
        <Sec title={`Недостаточно данных (${r.insufficient.length})`}>
          <div className="cv-table-wrap">
            <table className="cv-table inv2-table">
              <thead><tr><th>Позиция</th><th>Причина</th></tr></thead>
              <tbody>
                {r.insufficient.map((x, i) => (
                  <tr key={i}>
                    <td data-label="Позиция"><span className="cv-strong">{x.name}</span></td>
                    <td data-label="Причина" className="text-mute">{x.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Sec>
      )}

      {(r.conclusions || []).length > 0 && (
        <Sec title="Выводы">
          <ul className="cv-note-list cv-note-list--ok">
            {r.conclusions.map((s, i) => (
              <li key={i}><Icon name="check-circle" size={13} /><span className="text-dim">{s}</span></li>
            ))}
          </ul>
        </Sec>
      )}

      {(r.clarifications || []).length > 0 && (
        <div className="callout callout--info inv2-callout" role="note">
          <Icon name="info" size={15} />
          <div>{r.clarifications.map((s, i) => <div key={i}>{s}</div>)}</div>
        </div>
      )}

      {r.summary && (
        <div className="cv-callout report-verdict"><Icon name="sparkles" size={18} /><p>{r.summary}</p></div>
      )}
    </>
  );
}

export { AbcAnalysisReport };
